/**
 * Reading what a rep types into a date, a time or a date-and-time field.
 *
 * The fields are plain text inputs, because a rep answering "when can you take this call" types
 * `12.10 14:00` faster than they can walk five segments of a browser control, and pastes what a
 * booking page showed them. So this module is the control: it turns the text into the same stored
 * string the native inputs produced (`2026-10-12`, `14:00`, `2026-10-12T14:00`), or says why not.
 *
 * Three rules, each with a test in `tests/date-entry.test.ts`:
 *
 * - **Refuse, never bend.** `31.02` is refused with the month's length and `25:00` with the range.
 *   Nothing is clamped, rolled over or dropped: a word the reading cannot place refuses the whole
 *   entry, because a field that quietly ignores half of what was typed is promising a lead a time
 *   nobody wrote.
 * - **Day first, always.** The app writes dates the `en-GB` way everywhere (`app-dates.ts`), so
 *   `10/12` is the tenth of December for every reader. A numeric date that only works month first
 *   is refused with the day-first spelling of it, never accepted as a second convention.
 * - **Time is an argument.** A reading that needs today (`tomorrow`, a date with no year) takes it
 *   from its caller. Nothing here reads a clock, a zone or `Intl`; the zone work is in
 *   `app-dates.ts`.
 *
 * What a field shows for a stored value (`showDate` and its siblings) carries the weekday and the
 * month as a word, and reads back to the same value, so the text in the box is itself the check.
 */
import { appWeekdayDayMonth } from "./app-dates.ts";

/** What an entry turned out to be. Only `value` is ever stored. */
export type Reading =
  | { readonly kind: "empty" }
  | { readonly kind: "value"; readonly value: string }
  /** A draft that is not wrong yet: half a date-and-time, a day with no month. */
  | { readonly kind: "incomplete"; readonly message: string }
  | { readonly kind: "refused"; readonly message: string };

/** The day a relative entry is read against, as a stored calendar day in the field's own zone. */
export type ReadingContext = { readonly today: string };

type Civil = { readonly year: number; readonly month: number; readonly day: number };
type Clock = { readonly hour: number; readonly minute: number };
type Refusal = { readonly refused: string };

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

/*
 * The words a month or weekday may be written with, already folded to plain lower-case letters.
 * English because the interface is; Polish because the first reps are, and a date pasted from a
 * Polish page arrives in the genitive ("12 października").
 */
const MONTH_WORDS: readonly (readonly string[])[] = [
  ["january", "jan", "styczen", "stycznia", "sty"],
  ["february", "feb", "luty", "lutego", "lut"],
  ["march", "mar", "marzec", "marca"],
  ["april", "apr", "kwiecien", "kwietnia", "kwi"],
  ["may", "maj", "maja"],
  ["june", "jun", "czerwiec", "czerwca", "cze"],
  ["july", "jul", "lipiec", "lipca", "lip"],
  ["august", "aug", "sierpien", "sierpnia", "sie"],
  ["september", "sept", "sep", "wrzesien", "wrzesnia", "wrz"],
  ["october", "oct", "pazdziernik", "pazdziernika", "paz"],
  ["november", "nov", "listopad", "listopada", "lis"],
  ["december", "dec", "grudzien", "grudnia", "gru"],
];
/** Indexed the way `Date.getUTCDay` counts, Sunday first. */
const WEEKDAY_WORDS: readonly (readonly string[])[] = [
  ["sunday", "sun", "niedziela", "niedz"],
  ["monday", "mon", "poniedzialek", "pon"],
  ["tuesday", "tues", "tue", "wtorek", "wt"],
  ["wednesday", "wed", "sroda", "sr"],
  ["thursday", "thurs", "thur", "thu", "czwartek", "czw"],
  ["friday", "fri", "piatek", "pt"],
  ["saturday", "sat", "sobota", "sob"],
];
const TODAY_WORDS = ["today", "dzisiaj", "dzis"];
const TOMORROW_WORDS = ["tomorrow", "jutro"];
/** Words that join the parts of an entry and carry no value of their own. */
const FILLER = /\b(?:at|on|of|the|o|godz)\b/gu;
/** A zone named beside a time. The zone has its own field, so this is refused by name. */
const ZONE_WORD = /\b(?:utc|gmt|cet|cest|eet|eest|wet|west|bst|est|edt|cst|cdt|mst|mdt|pst|pdt)\b/u;

const longestFirst = (words: readonly (readonly string[])[]): string =>
  words
    .flat()
    .sort((a, b) => b.length - a.length)
    .join("|");
const MONTH = longestFirst(MONTH_WORDS);
const WEEKDAY = longestFirst(WEEKDAY_WORDS);
const indexOfWord = (words: readonly (readonly string[])[], word: string): number =>
  words.findIndex((group) => group.includes(word));

/** A year written with four digits that is plainly a year and not `1400` meant as a time. */
const YEAR_WORD = "(19\\d{2}|2[01]\\d{2})";
const YEAR_FLOOR = 2000;
const YEAR_CEILING = 2100;

const pad = (value: number, width = 2): string => String(value).padStart(width, "0");
const daysIn = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();
const weekdayOf = (date: Civil): number =>
  new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
const stored = (date: Civil): string => `${pad(date.year, 4)}-${pad(date.month)}-${pad(date.day)}`;
const storedClock = (clock: Clock): string => `${pad(clock.hour)}:${pad(clock.minute)}`;
const refuse = (message: string): Refusal => ({ refused: message });
const isRefusal = (value: object | null): value is Refusal => value !== null && "refused" in value;

/** The parts of a stored calendar day, or `null` when it is not one that exists. */
function civilOf(value: string): Civil | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (!match) return null;
  const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  if (date.month < 1 || date.month > 12) return null;
  if (date.day < 1 || date.day > daysIn(date.year, date.month)) return null;
  return date;
}

/** A stored calendar day moved by whole days. A value that is no day comes back unchanged. */
export function addDays(value: string, days: number): string {
  const date = civilOf(value);
  if (date === null) return value;
  const moved = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return stored({
    year: moved.getUTCFullYear(),
    month: moved.getUTCMonth() + 1,
    day: moved.getUTCDate(),
  });
}

/** Lower case, accents removed, spaces collapsed: the one spelling every table above is in. */
export function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replaceAll("ł", "l")
    .replace(/\s+/gu, " ")
    .trim();
}

/** The text still to be read. A part that is read is blanked in place, so nothing is read twice. */
type Rest = { text: string };

function take(rest: Rest, pattern: RegExp): RegExpExecArray | null {
  const match = pattern.exec(rest.text);
  if (!match) return null;
  rest.text =
    rest.text.slice(0, match.index) +
    " ".repeat(match[0].length) +
    rest.text.slice(match.index + match[0].length);
  return match;
}

/** A date whose year was written, checked against the calendar. */
function civil(year: number, month: number, day: number, numeric: boolean): Civil | Refusal {
  if (year < YEAR_FLOOR || year > YEAR_CEILING)
    return refuse(
      `The year ${year} is out of range. Type a year from ${YEAR_FLOOR} to ${YEAR_CEILING}.`,
    );
  const shape = dayAndMonth(month, day, numeric);
  if (shape) return shape;
  const length = daysIn(year, month);
  const name = MONTH_NAMES[month - 1];
  if (day > length)
    return refuse(`${name} ${year} has ${length} days, so ${day} ${name} does not exist.`);
  return { year, month, day };
}

/** The refusals that need no year: a month or a day number that no calendar has. */
function dayAndMonth(month: number, day: number, numeric: boolean): Refusal | null {
  if (month < 1 || month > 12) {
    // 10/14 is the fourteenth of October to an American reader. Say how this app writes it.
    const dayFirst =
      numeric && month <= 31 && day >= 1 && day <= 12
        ? ` Write the day first, like ${month}.${pad(day)}.`
        : "";
    return refuse(`There is no month ${month}.${dayFirst}`);
  }
  if (day < 1 || day > 31) return refuse(`There is no day ${day} in a month.`);
  return null;
}

/** A date written with no year: its next occurrence from today, today included. */
function upcoming(month: number, day: number, numeric: boolean, today: Civil): Civil | Refusal {
  const shape = dayAndMonth(month, day, numeric);
  if (shape) return shape;
  // 2028 is a leap year, so this is the longest the month ever is.
  if (day > daysIn(2028, month)) return refuse(`${MONTH_NAMES[month - 1]} never has ${day} days.`);
  // Eight years reach the next 29 February from any day, century years included.
  for (let year = today.year; year <= today.year + 8; year += 1) {
    if (day > daysIn(year, month)) continue;
    const candidate = { year, month, day };
    if (stored(candidate) >= stored(today)) return candidate;
  }
  return refuse(`Add the year to ${day} ${MONTH_NAMES[month - 1]}.`);
}

const yearOf = (written: string): number =>
  written.length === 2 ? 2000 + Number(written) : Number(written);

/** A clock reading, checked against the ranges a clock has. */
function clock(hour: number, minute: number, meridiem: string | undefined): Clock | Refusal {
  if (minute > 59) return refuse(`There is no minute ${minute}. Minutes run from 00 to 59.`);
  if (meridiem) {
    if (hour < 1 || hour > 12)
      return refuse(
        `${hour} ${meridiem.startsWith("a") ? "am" : "pm"} is not a time. With am or pm the hour runs from 1 to 12.`,
      );
    const afternoon = meridiem.startsWith("p");
    return { hour: (hour % 12) + (afternoon ? 12 : 0), minute };
  }
  if (hour > 23) return refuse(`There is no hour ${hour}. Hours run from 00 to 23.`);
  return { hour, minute };
}

const MERIDIEM = "(a\\.?m\\.?|p\\.?m\\.?)";
const COLON_TIME = new RegExp(
  `(?<![\\d:.])(\\d{1,2}):(\\d{2})(?::(\\d{2})(?:[.,]\\d+)?)?\\s*${MERIDIEM}?(?![\\d:a-z])`,
  "u",
);
const MERIDIEM_TIME = new RegExp(
  `(?<![\\d:.])(\\d{1,2})(?:\\.(\\d{2}))?\\s*${MERIDIEM}(?![a-z])`,
  "u",
);
const H_TIME = /(?<![\d:.])(\d{1,2})h(\d{2})?(?![a-z\d])/u;
const DOT_TIME = /(?<![\d:.])(\d{1,2})\.(\d{2})(?![\d:.])/u;
const DIGIT_TIME = /(?<![\d:.])(\d{1,2}?)(\d{2})(?![\d:.])/u;
const HOUR_ONLY = /(?<![\d:.])(\d{1,2})(?![\d:.])/u;

/** A time written so that it can be nothing else: `14:00`, `2pm`, `14h30`. */
function takeWrittenTime(rest: Rest): Clock | Refusal | null {
  const colon = take(rest, COLON_TIME);
  if (colon) {
    if (colon[3] !== undefined && colon[3] !== "00")
      return refuse("Seconds are not kept. Type the time to the minute, like 14:00.");
    return clock(Number(colon[1]), Number(colon[2]), colon[4]);
  }
  const meridiem = take(rest, MERIDIEM_TIME);
  if (meridiem) return clock(Number(meridiem[1]), Number(meridiem[2] ?? 0), meridiem[3]);
  const hours = take(rest, H_TIME);
  if (hours) return clock(Number(hours[1]), Number(hours[2] ?? 0), undefined);
  return null;
}

/** Digits that are a time only because nothing else is left for them to be: `14.00`, `1400`, `14`. */
function takeLooseTime(rest: Rest): Clock | Refusal | null {
  const dotted = take(rest, DOT_TIME);
  if (dotted) return clock(Number(dotted[1]), Number(dotted[2]), undefined);
  const digits = /(?<![\d:.])\d{3,4}(?![\d:.])/u.test(rest.text) ? take(rest, DIGIT_TIME) : null;
  if (digits) return clock(Number(digits[1]), Number(digits[2]), undefined);
  const hour = take(rest, HOUR_ONLY);
  if (hour) return clock(Number(hour[1]), 0, undefined);
  return null;
}

const DAY_MONTH_WORD = new RegExp(
  `(?<!\\d)(\\d{1,2})(?:st|nd|rd|th)?\\.?\\s*(?:of\\s+)?(${MONTH})\\b\\.?(?:,?\\s*${YEAR_WORD}(?!\\d))?`,
  "u",
);
const MONTH_WORD_DAY = new RegExp(
  `\\b(${MONTH})\\b\\.?\\s*(\\d{1,2})(?:st|nd|rd|th)?(?![\\d:])(?:,?\\s*${YEAR_WORD}(?!\\d))?`,
  "u",
);
const ISO_DATE = /(?<![\d./-])(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?!\d)/u;
const NUMERIC_DATE = /(?<![\d./-])(\d{1,2})[./-](\d{1,2})(?:[./-](\d{4}|\d{2}))?\.?(?!\d)/u;
const EIGHT_DIGITS = /(?<!\d)(\d{8})(?!\d)/u;
const WEEKDAY_WORD = new RegExp(`\\b(${WEEKDAY})\\b\\.?`, "u");
const TODAY_WORD = new RegExp(`\\b(?:${TODAY_WORDS.join("|")})\\b`, "u");
const TOMORROW_WORD = new RegExp(`\\b(?:${TOMORROW_WORDS.join("|")})\\b`, "u");

type DateReading = {
  readonly date: Civil | Refusal | null;
  /** A dotted pair that failed as a date beside a weekday and is a time instead: `wed 9.30`. */
  readonly time: Clock | null;
};

/**
 * The date in an entry, with whatever named it blanked out of `rest`.
 *
 * A date named in words is looked for before one written in digits, because digits beside
 * `tomorrow` or `12 Oct` are a time. A weekday is a check on a written date and a date of its own
 * only when nothing else names one.
 */
function takeDate(rest: Rest, today: Civil, whole: boolean): DateReading {
  const weekdayWord = take(rest, WEEKDAY_WORD);
  const weekday = weekdayWord ? indexOfWord(WEEKDAY_WORDS, weekdayWord[1] ?? "") : null;
  const checked = (date: Civil | Refusal | null): Civil | Refusal | null => {
    if (date === null || isRefusal(date) || weekday === null) return date;
    if (weekdayOf(date) === weekday) return date;
    return refuse(
      `${appWeekdayDayMonth(stored(date)).slice(4)} ${date.year} is a ${WEEKDAY_NAMES[weekdayOf(date)]}, not a ${WEEKDAY_NAMES[weekday]}.`,
    );
  };
  const named = (match: RegExpExecArray, day: string, month: string): Civil | Refusal => {
    const monthNumber = indexOfWord(MONTH_WORDS, month) + 1;
    return match[3] === undefined
      ? upcoming(monthNumber, Number(day), false, today)
      : civil(Number(match[3]), monthNumber, Number(day), false);
  };

  const dayFirst = take(rest, DAY_MONTH_WORD);
  if (dayFirst)
    return { date: checked(named(dayFirst, dayFirst[1] ?? "", dayFirst[2] ?? "")), time: null };
  const monthFirst = take(rest, MONTH_WORD_DAY);
  if (monthFirst)
    return {
      date: checked(named(monthFirst, monthFirst[2] ?? "", monthFirst[1] ?? "")),
      time: null,
    };
  if (take(rest, TODAY_WORD)) return { date: checked(today), time: null };
  if (take(rest, TOMORROW_WORD)) {
    const next = civilOf(addDays(stored(today), 1));
    return { date: checked(next), time: null };
  }

  const iso = take(rest, ISO_DATE);
  if (iso)
    return {
      date: checked(civil(Number(iso[1]), Number(iso[2]), Number(iso[3]), true)),
      time: null,
    };

  const numeric = take(rest, NUMERIC_DATE);
  if (numeric) {
    const [day, month] = [Number(numeric[1]), Number(numeric[2])];
    const written =
      numeric[3] === undefined
        ? upcoming(month, day, true, today)
        : civil(yearOf(numeric[3]), month, day, true);
    const agreed = checked(written);
    /*
     * "wed 9.30": the weekday is the date, and a dotted pair that is no date, or a date on another
     * weekday, is the time. Only with no year written; `12.10.2026` is never a time.
     */
    const dotted = /^\d{1,2}\.\d{2}$/u.test(numeric[0]);
    if (weekday !== null && numeric[3] === undefined && dotted && isRefusal(agreed)) {
      const asTime = clock(day, month, undefined);
      if (!isRefusal(asTime)) return { date: nextWeekday(today, weekday), time: asTime };
    }
    return { date: agreed, time: null };
  }

  const digits = take(rest, EIGHT_DIGITS);
  if (digits) return { date: checked(eightDigits(digits[1] ?? "")), time: null };
  if (whole) {
    // A date field holds nothing but a date, so four digits there are a day and a month.
    const four = take(rest, /^\s*(\d{2})(\d{2})\s*$/u);
    if (four)
      return { date: checked(upcoming(Number(four[2]), Number(four[1]), true, today)), time: null };
  }
  return { date: weekday === null ? null : nextWeekday(today, weekday), time: null };
}

/** The next day with that weekday strictly after today: said on a Monday, "Monday" is next week. */
function nextWeekday(today: Civil, weekday: number): Civil | null {
  const ahead = (weekday - weekdayOf(today) + 7) % 7 || 7;
  return civilOf(addDays(stored(today), ahead));
}

/** `12102026` as the native control was typed, or `20261012` as a system writes it. */
function eightDigits(digits: string): Civil | Refusal {
  const dayFirst = civil(
    Number(digits.slice(4)),
    Number(digits.slice(2, 4)),
    Number(digits.slice(0, 2)),
    true,
  );
  if (!isRefusal(dayFirst)) return dayFirst;
  const yearFirst = civil(
    Number(digits.slice(0, 4)),
    Number(digits.slice(4, 6)),
    Number(digits.slice(6)),
    true,
  );
  return isRefusal(yearFirst) ? dayFirst : yearFirst;
}

/** What is left once the date and time are read. Anything but joining words refuses the entry. */
function leftover(rest: Rest): Refusal | null {
  const remains = rest.text
    .replace(FILLER, " ")
    .replace(/[\s,.;@–—-]+/gu, " ")
    .trim();
  if (remains === "") return null;
  if (ZONE_WORD.test(remains))
    return refuse("A time zone goes in its own field. Type the time alone.");
  return refuse(`Could not read "${remains.slice(0, 24)}" as part of it.`);
}

const OFFSET = /\d{1,2}:\d{2}(?::\d{2}(?:[.,]\d+)?)?\s*(?:z\b|[+-]\d{2}:?\d{2}\b)/u;
const WITH_OFFSET =
  "This time carries its own UTC offset. Type it as it reads on the clock in the time zone you mean.";

const none: Reading = { kind: "empty" };
const reading = (value: string): Reading => ({ kind: "value", value });
const refusal = (from: Refusal): Reading => ({ kind: "refused", message: from.refused });
const incomplete = (message: string): Reading => ({ kind: "incomplete", message });

function todayOf(context: ReadingContext): Civil {
  const today = civilOf(context.today);
  if (today === null) throw new Error("the reading context's today is not a calendar day");
  return today;
}

/** A calendar day, stored as `2026-10-12`. */
export function readDate(text: string, context: ReadingContext): Reading {
  const rest: Rest = { text: fold(text) };
  if (rest.text === "") return none;
  if (/^\d{1,2}\.?$/u.test(rest.text))
    return incomplete(`Add the month, like ${rest.text.replace(".", "")}.10.`);
  const { date, time } = takeDate(rest, todayOf(context), true);
  if (isRefusal(date)) return refusal(date);
  if (time !== null) return { kind: "refused", message: "This field takes a date with no time." };
  const extra = leftover(rest);
  if (extra) return date === null ? notADate : refusal(extra);
  return date === null ? notADate : reading(stored(date));
}
const notADate: Reading = {
  kind: "refused",
  message: "That does not read as a date. Try 12.10.2026, 12 Oct or tomorrow.",
};

/** A time of day, stored as `14:00`. */
export function readTime(text: string): Reading {
  const rest: Rest = { text: fold(text) };
  if (rest.text === "") return none;
  const written = takeWrittenTime(rest) ?? takeLooseTime(rest);
  if (isRefusal(written)) return refusal(written);
  if (written === null || leftover(rest))
    return { kind: "refused", message: "That does not read as a time. Try 14:00 or 2pm." };
  return reading(storedClock(written));
}

/** A day and a time on it, stored as `2026-10-12T14:00`: a clock reading, with no zone of its own. */
export function readDateTime(text: string, context: ReadingContext): Reading {
  const folded = fold(text);
  if (folded === "") return none;
  if (/^\d{1,2}$/u.test(folded)) return incomplete("Type a date and a time, like 12.10 14:00.");
  if (OFFSET.test(folded)) return { kind: "refused", message: WITH_OFFSET };
  const digits = /^(\d{8})(\d{4})$/u.exec(folded);
  // `2026-10-12T14:00`, as a system writes it: the T only joins the two halves.
  const rest: Rest = {
    text: digits
      ? `${digits[1]} ${digits[2]}`
      : folded.replace(/(\d{4}-\d{2}-\d{2})t(?=\d)/u, "$1 "),
  };
  const written = takeWrittenTime(rest);
  const { date, time: dotted } = takeDate(rest, todayOf(context), false);
  const time = written ?? dotted ?? takeLooseTime(rest);
  if (isRefusal(date)) return refusal(date);
  if (isRefusal(time)) return refusal(time);
  const extra = leftover(rest);
  if (extra)
    return date === null && time === null
      ? {
          kind: "refused",
          message: "That does not read as a date and time. Try 12.10 14:00 or tomorrow 2pm.",
        }
      : refusal(extra);
  if (date !== null && time !== null) return reading(`${stored(date)}T${storedClock(time)}`);
  if (date !== null) return incomplete("Add a time, like 14:00.");
  return incomplete("Add a date, like 12.10 or tomorrow.");
}

/** The parts of a stored date-and-time, or `null` when it is not one. */
export function splitDateTime(value: string): { date: string; time: string } | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/u.exec(value);
  if (!match?.[1] || !match[2]) return null;
  return civilOf(match[1]) !== null && showTime(match[2]) !== ""
    ? { date: match[1], time: match[2] }
    : null;
}

/**
 * A length of time as a slot's end says it back: "15 minutes long", "2 days 15 minutes long".
 *
 * Whole units only and none left out but the empty ones, because the point of saying it is that a
 * slot meant as a quarter of an hour and entered as two days reads as two days.
 */
export function spanInWords(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  const whole = Math.round(minutes);
  const counted = (count: number, unit: string): string =>
    count === 0 ? "" : `${count} ${unit}${count === 1 ? "" : "s"}`;
  const parts = [
    counted(Math.floor(whole / 1440), "day"),
    counted(Math.floor((whole % 1440) / 60), "hour"),
    counted(whole % 60, "minute"),
  ].filter((part) => part !== "");
  return `${parts.join(" ")} long`;
}

/** A stored day as the field shows it: "Mon 12 Oct 2026". Nothing for what is not a stored day. */
export function showDate(value: string): string {
  const date = civilOf(value);
  return date === null ? "" : `${appWeekdayDayMonth(value)} ${date.year}`;
}

/** A stored time as the field shows it: "14:00". Nothing for what is not a stored time. */
export function showTime(value: string): string {
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(value) ? value : "";
}

/** A stored date-and-time as the field shows it: "Mon 12 Oct 2026, 14:00". */
export function showDateTime(value: string): string {
  const parts = splitDateTime(value);
  return parts === null ? "" : `${showDate(parts.date)}, ${parts.time}`;
}
